import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  plansService as service,
  agentService,
  contextService,
} from "../../services/api";
import toast from "react-hot-toast";
import PlanPlaces from "./PlanPlaces";
import PlanDeparture from "./PlanDeparture";
import RecordProposals from "../assistant/RecordProposals";

const kinds = ["trip", "learning", "purchase", "goal", "project"];
const stepKinds = [
  "task",
  "research",
  "contact",
  "booking",
  "purchase",
  "course",
  "activity",
  "milestone",
];
const blank = {
  title: "",
  kind: "goal",
  desired_outcome: "",
  status: "active",
  location: "",
  constraints: "",
  target_date: null,
  research_start: null,
  research_end: null,
  priority: "normal",
  person_ids: [],
  steps: [],
};
const newStep = () => ({
  key: crypto.randomUUID(),
  title: "",
  kind: "task",
  status: "open",
  required: true,
  owner: "",
  notes: "",
  outcome: "",
  due_at: null,
  days_before_target: null,
  depends_on: [],
  links: [],
  research: null,
});
const localInput = (value) =>
  value
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
const displayDate = (value, zone) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        timeZone: zone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Needs a date";
function errorText(error) {
  const detail = error.response?.data?.detail;
  return typeof detail === "string"
    ? detail
    : Array.isArray(detail)
      ? detail.map((item) => item.msg).join("; ")
      : error.message || "Could not save the plan";
}
function useRequest() {
  const ref = useRef(null);
  return (values) => {
    const text = JSON.stringify(values);
    if (text !== ref.current?.text)
      ref.current = { text, id: crypto.randomUUID() };
    return { ...values, request_id: ref.current.id };
  };
}
function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}
function Picker({ resource, onPick, excluded = [] }) {
  const [text, setText] = useState("");
  const query = useQuery({
    queryKey: ["plan-options", resource, text],
    queryFn: () => service.options(resource, text),
  });
  return (
    <div className="space-y-2">
      <input
        aria-label={`Search ${resource.replaceAll("_", " ")}`}
        className="field-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Search ${resource.replaceAll("_", " ")}`}
      />
      <select
        className="field-input"
        aria-label={`Choose ${resource.replaceAll("_", " ")}`}
        value=""
        onChange={(e) => {
          const row = query.data?.items.find(
            (item) => item.id === Number(e.target.value),
          );
          if (row) onPick(row);
        }}
      >
        <option value="">
          {query.isLoading ? "Loading…" : "Choose a saved record"}
        </option>
        {query.data?.items
          .filter((item) => !excluded.includes(item.id))
          .map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
              {item.status === "cancelled" ? " (cancelled)" : ""}
            </option>
          ))}
      </select>
      {query.error && (
        <p role="alert">
          Records could not be loaded.{" "}
          <button type="button" onClick={() => query.refetch()}>
            Retry
          </button>
        </p>
      )}
      {query.data?.more && (
        <p className="text-xs text-[#66716a]">
          Showing 30 matches; narrow your search.
        </p>
      )}
    </div>
  );
}
function starter(kind) {
  const titles = {
    trip: [
      "Research routes and seasonal conditions",
      "Contact the provider about availability",
      "Confirm arrangements",
      "Complete the trip",
    ],
    learning: [
      "Compare courses and prerequisites",
      "Confirm dates and availability",
      "Enroll in the chosen course",
      "Review learning progress",
    ],
    purchase: [
      "Research suitable options",
      "Visit or contact a store",
      "Record the purchase and next steps",
    ],
  }[kind] || [
    "Define the next milestone",
    "Complete the work",
    "Review the outcome",
  ];
  return titles.map((title, index) => ({
    ...newStep(),
    key: `step-${index + 1}`,
    title,
    kind: index === 0 ? "research" : index === 1 ? "contact" : "milestone",
    depends_on: index ? [`step-${index}`] : [],
  }));
}
function PlanSettings({
  initial,
  people = [],
  onSave,
  onCancel,
  pending,
  error,
  creating,
}) {
  const [form, setForm] = useState(initial);
  const [names, setNames] = useState(
    Object.fromEntries(people.map((p) => [p.id, p.name])),
  );
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  return (
    <form
      className="surface-panel p-5 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <h2 className="text-lg font-bold">
        {creating ? "New plan" : "Plan settings"}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Plan title">
          <input
            required
            maxLength={180}
            className="field-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>
        <Field label="Plan kind">
          <select
            className="field-input"
            value={form.kind}
            onChange={(e) => set("kind", e.target.value)}
          >
            {kinds.map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Desired outcome">
        <textarea
          className="field-input"
          maxLength={2000}
          value={form.desired_outcome}
          onChange={(e) => set("desired_outcome", e.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Target date (optional)">
          <input
            type="date"
            className="field-input"
            value={form.target_date || ""}
            onChange={(e) => set("target_date", e.target.value || null)}
          />
        </Field>
        <Field label="Priority">
          <select
            className="field-input"
            value={form.priority}
            onChange={(e) => set("priority", e.target.value)}
          >
            {["critical", "high", "normal", "low"].map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        </Field>
        <Field label="Plan status">
          <select
            className="field-input"
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            {["active", "paused", "completed", "cancelled"].map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Location">
        <input
          className="field-input"
          maxLength={240}
          value={form.location}
          onChange={(e) => set("location", e.target.value)}
        />
      </Field>
      <Field label="Constraints and preparation notes">
        <textarea
          className="field-input"
          maxLength={4000}
          rows={3}
          value={form.constraints}
          onChange={(e) => set("constraints", e.target.value)}
          placeholder="Travel mode, season, budget, accessibility or course prerequisites"
        />
      </Field>
      <div>
        <p className="field-label">People involved</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.person_ids.map((id) => (
            <button
              key={id}
              type="button"
              className="secondary-button text-xs"
              onClick={() =>
                set(
                  "person_ids",
                  form.person_ids.filter((p) => p !== id),
                )
              }
            >
              Remove {names[id] || `person #${id}`}
            </button>
          ))}
        </div>
        <Picker
          resource="person"
          excluded={form.person_ids}
          onPick={(p) => {
            setNames((old) => ({ ...old, [p.id]: p.title }));
            set("person_ids", [...form.person_ids, p.id]);
          }}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Research starts (optional)">
          <input
            type="date"
            className="field-input"
            value={form.research_start || ""}
            onChange={(e) => set("research_start", e.target.value || null)}
          />
        </Field>
        <Field label="Research ends (optional)">
          <input
            type="date"
            className="field-input"
            value={form.research_end || ""}
            onChange={(e) => set("research_end", e.target.value || null)}
          />
        </Field>
      </div>
      <p className="text-xs text-[#66716a]">
        Research uses each step’s explicit search request and location. It ends
        on the target date unless you set a different end date. Relative step
        deadlines fall at 09:00 in your account timezone.
      </p>
      {creating && (
        <div>
          <button
            type="button"
            className="secondary-button"
            onClick={() => set("steps", starter(form.kind))}
          >
            Use editable starter steps
          </button>
          {form.steps.length > 0 && (
            <ol className="list-decimal pl-5 mt-3 text-sm space-y-1">
              {form.steps.map((s) => (
                <li key={s.key}>{s.title}</li>
              ))}
            </ol>
          )}
          <p className="mt-2 text-xs text-[#66716a]">
            Starter steps have no deadlines. Edit them after creating the plan,
            or describe your plan in Ask.
          </p>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {errorText(error)}
        </p>
      )}
      <div className="flex gap-3">
        <button disabled={pending} className="primary-button">
          {pending ? "Saving…" : creating ? "Create plan" : "Save settings"}
        </button>
        <button
          disabled={pending}
          type="button"
          className="secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
function StepEditor({
  initial,
  steps,
  timezone,
  linkedRecords = [],
  onSave,
  onCancel,
  pending,
  error,
}) {
  const [form, setForm] = useState(initial);
  const [fixedDate, setFixedDate] = useState(localInput(initial.due_at));
  const [dateMode, setDateMode] = useState(
    initial.days_before_target !== null
      ? "relative"
      : initial.due_at
        ? "fixed"
        : "none",
  );
  const [resource, setResource] = useState("event");
  const [names, setNames] = useState(
    Object.fromEntries(
      linkedRecords.map((l) => [`${l.resource}-${l.id}`, l.title]),
    ),
  );
  const [localError, setLocalError] = useState("");
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const research = (key, value) =>
    set("research", { ...form.research, [key]: value });
  return (
    <form
      className="surface-panel p-5 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        setLocalError("");
        let due = null;
        if (dateMode === "fixed") {
          const parsed = new Date(fixedDate);
          if (
            Number.isNaN(parsed.getTime()) ||
            localInput(parsed.toISOString()) !== fixedDate
          ) {
            setLocalError("Choose a valid local deadline.");
            return;
          }
          due = parsed.toISOString();
        }
        onSave({
          ...form,
          due_at: due,
          days_before_target:
            dateMode === "relative"
              ? Number(form.days_before_target ?? 0)
              : null,
        });
      }}
    >
      <h2 className="text-lg font-bold">Edit plan step</h2>
      <Field label="Step title">
        <input
          className="field-input"
          required
          maxLength={180}
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Step kind">
          <select
            className="field-input"
            value={form.kind}
            onChange={(e) => set("kind", e.target.value)}
          >
            {stepKinds.map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        </Field>
        <Field label="Step status">
          <select
            className="field-input"
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            {["open", "waiting", "done", "cancelled"].map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
        </Field>
        <Field label="Owner or waiting on">
          <input
            className="field-input"
            maxLength={180}
            value={form.owner}
            onChange={(e) => set("owner", e.target.value)}
          />
        </Field>
      </div>
      <label className="flex gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.required}
          onChange={(e) => set("required", e.target.checked)}
        />
        Required to complete the plan
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Deadline type">
          <select
            className="field-input"
            value={dateMode}
            onChange={(e) => setDateMode(e.target.value)}
          >
            <option value="none">Date not known yet</option>
            <option value="relative">Days before target date</option>
            <option value="fixed">Fixed date and time</option>
          </select>
        </Field>
        {dateMode === "relative" && (
          <Field label={`Days before target (09:00 ${timezone})`}>
            <input
              type="number"
              min={0}
              max={3650}
              required
              className="field-input"
              value={form.days_before_target ?? 0}
              onChange={(e) => set("days_before_target", e.target.value)}
            />
          </Field>
        )}
        {dateMode === "fixed" && (
          <Field
            label={`Deadline (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
          >
            <input
              className="field-input"
              required
              type="datetime-local"
              value={fixedDate}
              onChange={(e) => setFixedDate(e.target.value)}
            />
          </Field>
        )}
      </div>
      <fieldset>
        <legend className="field-label">Prerequisites</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {steps
            .filter((s) => s.key !== form.key)
            .map((s) => (
              <label key={s.key} className="text-sm flex gap-2">
                <input
                  type="checkbox"
                  checked={form.depends_on.includes(s.key)}
                  onChange={(e) =>
                    set(
                      "depends_on",
                      e.target.checked
                        ? [...form.depends_on, s.key]
                        : form.depends_on.filter((k) => k !== s.key),
                    )
                  }
                />
                {s.title}
              </label>
            ))}
        </div>
      </fieldset>
      <Field label="Preparation notes">
        <textarea
          className="field-input"
          rows={3}
          maxLength={4000}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
      <Field label="Actual result or cancellation reason">
        <textarea
          className="field-input"
          required={["done", "cancelled"].includes(form.status)}
          maxLength={2000}
          value={form.outcome}
          onChange={(e) => set("outcome", e.target.value)}
          placeholder="Record what happened; saving a step does not book, buy or contact anyone."
        />
      </Field>
      <div>
        <p className="field-label">Linked calendar events, notes, email and documents</p>
        <p className="text-xs text-[#66716a] mb-2">Linked notes, monitored documents and analysed email can propose step updates for your review. Calendar links provide context.</p>
        <div className="space-y-2 mb-2">
          {form.links.map((l) => (
            <div
              key={`${l.resource}-${l.id}`}
              className="flex justify-between text-sm"
            >
              <span>
                {names[`${l.resource}-${l.id}`] ||
                  `${l.resource.replaceAll("_", " ")} #${l.id}`}
              </span>
              <button
                className="quiet-button"
                type="button"
                onClick={() =>
                  set(
                    "links",
                    form.links.filter((v) => v !== l),
                  )
                }
              >
                Remove link
              </button>
            </div>
          ))}
        </div>
        <select
          aria-label="Record type"
          className="field-input mb-2"
          value={resource}
          onChange={(e) => setResource(e.target.value)}
        >
          <option value="event">Calendar event</option>
          <option value="note">Note</option>
          <option value="mail_thread">Email conversation</option>
          <option value="source">Monitored document or Notion source</option>
        </select>
        <Picker
          resource={resource}
          excluded={form.links
            .filter((l) => l.resource === resource)
            .map((l) => l.id)}
          onPick={(r) => {
            set("links", [...form.links, { resource, id: r.id }]);
            setNames((old) => ({ ...old, [`${resource}-${r.id}`]: r.title }));
          }}
        />
      </div>
      <label className="flex gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          checked={!!form.research}
          onChange={(e) =>
            set(
              "research",
              e.target.checked
                ? {
                    title: form.title || "Plan research",
                    query: "",
                    kind: form.kind === "course" ? "course" : "general",
                    location: "",
                    duration_minutes: null,
                    local_time: "16:00",
                    weekdays: [0, 1, 2, 3, 4, 5, 6],
                    enabled: true,
                  }
                : null,
            )
          }
        />
        Schedule research for this step
      </label>
      {form.research && (
        <div className="bg-[#f4f8f5] border rounded-md p-4 space-y-3">
          <Field label="Public research request">
            <textarea
              className="field-input"
              required
              minLength={5}
              maxLength={2000}
              value={form.research.query}
              onChange={(e) => research("query", e.target.value)}
              placeholder="What should the assistant research?"
            />
          </Field>
          <p className="text-xs text-[#66716a]">
            Only this query and research location are used for the search. Keep
            private names, contact details and account information out of it.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Research category">
              <select
                className="field-input"
                value={form.research.kind}
                onChange={(e) => research("kind", e.target.value)}
              >
                {[
                  "general",
                  "travel",
                  "course",
                  "gear",
                  "activity",
                  "gift",
                  "podcast",
                  "movie",
                ].map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </Field>
            <Field label="Research location">
              <input
                className="field-input"
                maxLength={240}
                value={form.research.location}
                onChange={(e) => research("location", e.target.value)}
              />
            </Field>
            <Field label={`Research time (${timezone})`}>
              <input
                className="field-input"
                type="time"
                required
                value={form.research.local_time}
                onChange={(e) => research("local_time", e.target.value)}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-3">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => (
              <label className="text-xs flex gap-1" key={day}>
                <input
                  type="checkbox"
                  checked={form.research.weekdays.includes(i)}
                  onChange={(e) =>
                    research(
                      "weekdays",
                      e.target.checked
                        ? [...form.research.weekdays, i].sort()
                        : form.research.weekdays.filter((d) => d !== i),
                    )
                  }
                />
                {day}
              </label>
            ))}
          </div>
          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.research.enabled}
              onChange={(e) => research("enabled", e.target.checked)}
            />
            Enable when this step is ready
          </label>
        </div>
      )}
      {(error || localError) && (
        <p role="alert" className="text-sm text-red-700">
          {localError || errorText(error)}
        </p>
      )}
      <div className="flex gap-3">
        <button className="primary-button" disabled={pending}>
          {pending ? "Saving…" : "Save step"}
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={pending}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
function PlanHistory({ id }) {
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ["plan-history", id, offset],
    queryFn: () => service.history(id, offset),
  });
  return (
    <section className="surface-panel p-5 space-y-3">
      <h2 className="font-bold">Saved history</h2>
      {query.isLoading && <p>Loading history…</p>}
      {query.error && <p role="alert">History unavailable.</p>}
      {query.data?.items.map((item) => (
        <details key={item.revision} className="border-b pb-2">
          <summary className="cursor-pointer text-sm">
            Revision {item.revision} · {displayDate(item.saved_at, "UTC")} UTC
          </summary>
          <p className="text-sm mt-2">
            {item.definition.title} · {item.definition.status}
          </p>
          <ol className="list-decimal pl-5 text-sm space-y-2 mt-2">
            {item.definition.steps.map((s) => (
              <li key={s.key}>
                {s.title} · {s.status}
                {s.outcome && <p>{s.outcome}</p>}
              </li>
            ))}
          </ol>
        </details>
      ))}
      <div className="flex gap-3">
        {offset > 0 && (
          <button
            className="secondary-button"
            onClick={() => setOffset(Math.max(0, offset - 20))}
          >
            Newer history
          </button>
        )}
        {query.data?.next_offset !== null &&
          query.data?.next_offset !== undefined && (
            <button
              className="secondary-button"
              onClick={() => setOffset(query.data.next_offset)}
            >
              Older history
            </button>
          )}
      </div>
    </section>
  );
}
function Brief({ brief }) {
  const safe = (value) => {
    try {
      return ["https:", "http:"].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  };
  return (
    <details className="mt-3 rounded-md bg-[#f4f8f5] p-3">
      <summary className="text-sm font-semibold cursor-pointer">
        {brief.expired ? "Previous research (expired)" : "Latest research"}:{" "}
        {brief.title}
      </summary>
      <p className="whitespace-pre-wrap text-sm mt-3">{brief.content}</p>
      <ul className="mt-2 text-xs space-y-2">
        {brief.citations
          ?.filter((c) => safe(c.url))
          .map((c, i) => (
            <li key={i}>
              <a
                className="underline"
                href={c.url}
                target="_blank"
                rel="noreferrer"
              >
                {c.title || c.url}
              </a>
            </li>
          ))}
      </ul>
    </details>
  );
}
export function PlanWorkspace({ planId }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["plan", planId],
    queryFn: () => service.get(planId),
    refetchInterval: 60000,
  });
  const [editor, setEditor] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showPlaces, setShowPlaces] = useState(false);
  const request = useRequest();
  const refresh = () =>
    [
      "plan",
      "plans",
      "plan-history",
      "context",
      "contexts",
      "assistant",
      "today",
      "agent",
      "record-proposals",
    ].forEach((k) => client.invalidateQueries({ queryKey: [k] }));
  const mutation = useMutation({
    mutationFn: (payload) => service.update(planId, request(payload)),
    onSuccess: () => {
      setEditor(null);
      refresh();
      toast.success("Plan saved");
    },
  });
  const run = useMutation({
    mutationFn: (id) => agentService.runWatch(id),
    onSuccess: () => {
      toast.success("Research queued; results appear here when ready.");
      refresh();
    },
    onError: (e) => toast.error(errorText(e)),
  });
  const recordChange = useMutation({
    mutationFn: (operation) => operation(),
    onSuccess: refresh,
    onError: (e) => toast.error(errorText(e)),
  });
  if (query.isLoading) return <p role="status">Loading plan…</p>;
  if (query.error)
    return (
      <p role="alert">
        Plan could not be loaded.{" "}
        <button className="secondary-button" onClick={() => query.refetch()}>
          Retry
        </button>
      </p>
    );
  const data = query.data,
    def = data.definition;
  const open = (type, step) => {
    mutation.reset();
    setEditor({ type, step, base: data });
  };
  const save = (definition) =>
    mutation.mutate({ expected_revision: editor.base.revision, definition });
  return (
    <>
      <header className="page-header">
        <div>
          <Link to="/plans" className="block w-fit text-sm text-[#246b52]">
            ← All plans
          </Link>
          <p className="eyebrow mt-4">
            {def.kind} · {def.status}
          </p>
          <h1 className="page-title">{def.title}</h1>
          <p className="page-description">
            {def.desired_outcome ||
              "Define the outcome, then move through the next steps."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            className="secondary-button"
            to={`/contexts/${data.context_id}`}
          >
            Open context
          </Link>
          <button
            className="secondary-button"
            onClick={() => open("settings")}
            disabled={!!editor}
          >
            Edit plan
          </button>
        </div>
      </header>
      {(data.automatic_enabled === false || data.worker_enabled === false) && (
        <p className="surface-panel p-4 mb-4 text-sm" role="status">
          {data.worker_enabled === false
            ? "Background monitoring is disabled on this server."
            : "Automatic assistant checks are off."}{" "}
          You can still update plans and request research manually.{" "}
          <Link
            className="underline text-[#246b52]"
            to="/assistant?view=settings"
          >
            Open assistant settings
          </Link>
        </p>
      )}
      {data.context_status !== "active" && (
        <p role="status" className="surface-panel p-4 mb-4">
          The linked context is {data.context_status}. Reminders and research
          are paused.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-4 mb-5">
        {[
          [
            `${data.progress.completed}/${data.progress.total}`,
            "steps completed",
          ],
          [data.progress.ready, "steps ready"],
          [data.progress.overdue, "steps overdue"],
          [def.target_date || "Not set", "target date"],
        ].map(([v, label]) => (
          <div key={label} className="surface-panel p-4">
            <p className="text-xl font-bold">{v}</p>
            <p className="text-xs text-[#66716a] mt-1">{label}</p>
          </div>
        ))}
      </div>
      {editor ? (
        <div className="mb-5">
          {editor.type === "settings" ? (
            <PlanSettings
              initial={editor.base.definition}
              people={editor.base.people}
              onSave={save}
              onCancel={() => setEditor(null)}
              pending={mutation.isPending}
              error={mutation.error}
            />
          ) : (
            <StepEditor
              initial={editor.step}
              steps={editor.base.definition.steps}
              timezone={data.timezone}
              linkedRecords={
                editor.base.steps.find((s) => s.key === editor.step.key)
                  ?.linked_records || []
              }
              pending={mutation.isPending}
              error={mutation.error}
              onCancel={() => setEditor(null)}
              onSave={(step) =>
                save({
                  ...editor.base.definition,
                  steps: editor.base.definition.steps.some(
                    (s) => s.key === step.key,
                  )
                    ? editor.base.definition.steps.map((s) =>
                        s.key === step.key ? step : s,
                      )
                    : [...editor.base.definition.steps, step],
                })
              }
            />
          )}
          {mutation.error?.response?.status === 409 && (
            <button
              className="secondary-button mt-3"
              onClick={() => {
                setEditor(null);
                mutation.reset();
                query.refetch();
              }}
            >
              Discard editor changes and reload latest plan
            </button>
          )}
        </div>
      ) : null}
      <button className="secondary-button mb-4" aria-expanded={showPlaces} onClick={() => setShowPlaces(!showPlaces)}>{showPlaces ? "Hide places and route" : "Places and route"}</button>
      <PlanDeparture key={`departure-${data.id}`} plan={data} onSaved={refresh} disabled={!!editor} />
      {showPlaces && <PlanPlaces key={data.id} plan={data} onSaved={refresh} disabled={!!editor} />}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <section className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="font-bold text-lg">Steps and follow-through</h2>
            <button
              className="primary-button"
              disabled={!!editor || def.steps.length >= 60}
              onClick={() => open("step", newStep())}
            >
              Add step
            </button>
          </div>
          {!data.steps.length && (
            <p className="surface-panel p-5 text-sm">
              Add a concrete next step, or describe the plan in Ask. Unknown
              dates stay visible for review.
            </p>
          )}
          {data.steps.map((s, i) => (
            <article key={s.key} className="surface-panel p-5">
              <div className="flex justify-between gap-4">
                <div>
                  <p className="text-xs text-[#66716a] mb-2">
                    Step {i + 1} · {s.kind} ·{" "}
                    {s.required ? "required" : "optional"}
                  </p>
                  <h3 className="font-bold">{s.title}</h3>
                </div>
                <span
                  className={`status-pill self-start shrink-0 ${s.status === "done" && !s.dependency_conflict ? "status-pill-success" : s.overdue || s.dependency_conflict ? "status-pill-warning" : "status-pill-info"}`}
                >
                  {s.dependency_conflict
                    ? "Needs review"
                    : s.status === "done" || s.status === "cancelled"
                      ? s.status
                      : s.blocked_by.length
                        ? "Blocked"
                        : s.status === "waiting"
                          ? "Waiting"
                          : "Ready"}
                </span>
              </div>
              <p className="text-xs mt-3 text-[#66716a]">
                {displayDate(s.effective_due_at, data.timezone)}
                {s.effective_due_at ? ` (${data.timezone})` : ""}
                {s.days_before_target !== null
                  ? ` · ${s.days_before_target} days before target`
                  : ""}
                {s.owner ? ` · Owner: ${s.owner}` : ""}
              </p>
              {s.blocked_by.length > 0 && (
                <p className="text-sm mt-3 text-amber-800">
                  {s.dependency_conflict
                    ? "Completion recorded; prerequisite reopened or changed: "
                    : "Waiting for: "}
                  {s.blocked_by.join(", ")}
                </p>
              )}
              {s.notes && (
                <p className="text-sm whitespace-pre-wrap mt-3">{s.notes}</p>
              )}
              {s.outcome && (
                <p className="mt-3 text-sm rounded-md bg-[#f4f8f5] p-3">
                  <strong>Recorded outcome:</strong> {s.outcome}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {s.linked_records.map((l) =>
                  l.available ? (
                    <Link
                      key={`${l.resource}-${l.id}`}
                      className="text-xs underline"
                      to={l.url}
                    >
                      {l.title}
                      {l.status === "cancelled" ? " (cancelled)" : ""}
                    </Link>
                  ) : (
                    <span className="text-xs" key={`${l.resource}-${l.id}`}>
                      Linked record unavailable
                    </span>
                  ),
                )}
              </div>
              {s.research && (
                <p className="mt-3 text-xs text-[#66716a]">
                  Research{" "}
                  {s.research_state.eligible
                    ? `scheduled at ${s.research.local_time} (${data.timezone}) on selected days`
                    : "paused by step, prerequisites, plan or research window"}
                  .
                </p>
              )}
              {s.research_state.last_run && (
                <p className="mt-2 text-xs text-[#66716a]">
                  Last research:{" "}
                  {s.research_state.last_run.status.replaceAll("_", " ")}
                  {s.research_state.last_run.error
                    ? ` · ${s.research_state.last_run.error}`
                    : ""}
                  .{" "}
                  <Link className="underline" to="/assistant?view=agent">
                    View research activity
                  </Link>
                </p>
              )}
              {s.research_state.brief && (
                <Brief brief={s.research_state.brief} />
              )}
              <div className="mt-4 flex gap-3">
                <button
                  className="secondary-button text-xs"
                  disabled={!!editor}
                  onClick={() =>
                    open(
                      "step",
                      def.steps.find((step) => step.key === s.key),
                    )
                  }
                >
                  Edit {s.title}
                </button>
                {s.research_state.eligible && (
                  <button
                    className="secondary-button text-xs"
                    disabled={run.isPending}
                    onClick={() => run.mutate(s.research_state.watch_id)}
                  >
                    Research now
                  </button>
                )}
              </div>
            </article>
          ))}
        </section>
        <aside className="space-y-4">
          <section className="surface-panel p-5">
            <h2 className="font-bold">Plan details</h2>
            <p className="mt-3 text-sm">{def.location || "No location set"}</p>
            {data.people.length > 0 && (
              <p className="mt-3 text-sm">
                With {data.people.map((p) => p.name).join(", ")}
              </p>
            )}
            <p className="text-sm whitespace-pre-wrap mt-3">
              {def.constraints ||
                "Add constraints, preparation notes and season preferences in plan settings."}
            </p>
            <p className="mt-4 text-xs text-[#66716a]">
              {data.progress.can_complete
                ? "Required steps are done. Review the outcome and mark the plan completed in settings."
                : "Required steps and prerequisite conflicts must be resolved before this plan can be completed."}
            </p>
          </section>
          <section className="surface-panel p-5">
            <h2 className="font-bold">Keep it moving</h2>
            <p className="text-sm text-[#66716a] mt-2">
              Dates, missing prerequisites and overdue steps appear in Today.
              Link the actual email, event, document or note to keep evidence alongside
              the step.
            </p>
            <Link className="secondary-button mt-4" to="/ai">
              Plan with Ask
            </Link>
            <button
              className="quiet-button text-xs mt-3"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? "Hide history" : "View saved history"}
            </button>
          </section>
        </aside>
      </div>
      <div className="mt-5">
        <RecordProposals planId={planId} run={recordChange.mutate} busy={recordChange.isPending || mutation.isPending} />
      </div>
      {showHistory && (
        <div className="mt-5">
          <PlanHistory id={planId} />
        </div>
      )}
    </>
  );
}
export function PlansList() {
  const [params] = useSearchParams();
  const contextId = Number(params.get("context")) || null;
  const [creating, setCreating] = useState(!!contextId);
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ["plans", offset],
    queryFn: () => service.list(offset),
  });
  const context = useQuery({
    queryKey: ["context", String(contextId)],
    queryFn: () => contextService.getContext(contextId),
    enabled: !!contextId,
  });
  const navigate = useNavigate(),
    client = useQueryClient(),
    request = useRequest();
  const mutation = useMutation({
    mutationFn: (definition) =>
      service.create(request({ definition, context_id: contextId })),
    onSuccess: (row) => {
      ["plans", "contexts", "context", "assistant", "today"].forEach((k) =>
        client.invalidateQueries({ queryKey: [k] }),
      );
      navigate(`/plans/${row.id}`);
    },
  });
  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Work and life, step by step</p>
          <h1 className="page-title">Plans</h1>
          <p className="page-description">
            Trips, learning, purchases and goals with dates, prerequisites and
            follow-through.
          </p>
        </div>
        <button
          className="primary-button"
          disabled={creating}
          onClick={() => {
            mutation.reset();
            setCreating(true);
          }}
        >
          New plan
        </button>
      </header>
      {creating && (!contextId || context.data) && (
        <div className="mb-5">
          <PlanSettings
            key={contextId || "new"}
            creating
            initial={{
              ...blank,
              ...(context.data
                ? {
                    title: context.data.title,
                    desired_outcome: context.data.desired_outcome || "",
                    kind:
                      context.data.context_type === "trip" ? "trip" : "goal",
                  }
                : {}),
            }}
            pending={mutation.isPending}
            error={mutation.error}
            onSave={(v) => mutation.mutate(v)}
            onCancel={() => {
              setCreating(false);
              if (contextId) navigate("/plans");
            }}
          />
        </div>
      )}
      {context.error && (
        <p role="alert">The linked context could not be loaded.</p>
      )}
      {query.isLoading ? (
        <p role="status">Loading plans…</p>
      ) : query.error ? (
        <p role="alert">
          Plans unavailable.{" "}
          <button className="secondary-button" onClick={() => query.refetch()}>
            Retry
          </button>
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {query.data.items.map((row) => (
            <Link
              key={row.id}
              to={`/plans/${row.id}`}
              className="surface-panel p-5 hover:bg-[#f8faf8]"
            >
              <div className="flex justify-between gap-2 text-xs">
                <span className="capitalize text-[#66716a]">
                  {row.definition.kind}
                </span>
                <span className="status-pill status-pill-info">
                  {row.definition.status}
                </span>
              </div>
              <h2 className="font-bold text-lg mt-4">{row.definition.title}</h2>
              <p className="mt-2 text-sm text-[#66716a]">
                {row.definition.desired_outcome || "Outcome not defined yet"}
              </p>
              <p className="mt-5 text-xs">
                {row.progress.completed}/{row.progress.total} completed ·{" "}
                {row.progress.ready} ready · {row.progress.overdue} overdue
              </p>
            </Link>
          ))}
        </div>
      )}
      {query.data?.items.length === 0 && (
        <p className="surface-panel p-6">
          Create a plan to track its next steps, or ask the assistant to build
          one from your instructions.
        </p>
      )}
      <div className="flex gap-3 mt-5">
        {offset > 0 && (
          <button
            className="secondary-button"
            onClick={() => setOffset(offset - 30)}
          >
            Previous plans
          </button>
        )}
        {query.data?.next_offset != null && (
          <button
            className="secondary-button"
            onClick={() => setOffset(query.data.next_offset)}
          >
            More plans
          </button>
        )}
      </div>
    </>
  );
}
export default function PlansPage() {
  const { planId } = useParams();
  return (
    <div className="app-page">
      <div className="app-page-inner max-w-6xl">
        {planId ? (
          <PlanWorkspace key={planId} planId={Number(planId)} />
        ) : (
          <PlansList />
        )}
      </div>
    </div>
  );
}
